import { extractApiError } from '@/api/errorStatus';
import type { BillingTier, TierLimits, TierUsage } from '@/types/billing';

export interface TierLimitRejection {
  /** Сколько сущностей уже есть — для текста «использовано N из M». */
  usage: number;
  /** Сколько разрешает тариф. */
  limit: number;
  /**
   * WEB-1908 (возврат приёмки В11-Л6): ПРИЧИНА отказа — `PAYMENT_REQUIRED_*`
   * из тела ответа. Вызывающий уводит человека в окно тарифа В ЭТОЙ ЖЕ ветке
   * и до строки показа сообщения не доходит; без причины прямо здесь окну
   * нечего сказать о том, что именно упёрлось. Общий `code` по-прежнему
   * выбирает окно, причина — только текст. `undefined` у старого сервера.
   */
  reasonCode?: string;
}

/**
 * WEB-911: сервер отдаёт один и тот же отказ по лимиту тарифа (402 с кодом
 * TIER_LIMIT) со ВСЕХ путей появления новой сущности — обычного создания,
 * создания из шаблона, дублирования, копирования в другой проект и генерации
 * ИИ. Раньше страницы ловили его только на обычном создании, а на остальных
 * путях показывали техническое «Не удалось создать, попробуйте ещё раз» —
 * человек не понимал, что упёрся в тариф, и чинить ему было нечего.
 *
 * Возвращает числа лимита, если это отказ по тарифу, и null — если обычная
 * ошибка (её показываем текстом, как раньше).
 *
 * Статус 402 без кода тоже считаем отказом по лимиту: код может не доехать
 * (шрам WEB-135 — перехватчик когда-то терял и статус, и код). Ветка
 * оставлена осознанно: это страховка от потери кода по дороге, а не признак
 * того, что сервер шлёт безкодовые 402 штатно.
 *
 * WEB-1495: но 402 с НЕТАРИФНЫМ кодом — не отказ по тарифу. Сервер отдаёт
 * 402 и про другое: PROJECT_BLOCKED (ревизия пачек 6-7 переписала его текст:
 * «отрицательного баланса кошелька» не бывает по построению, настоящие причины
 * — задолженность организации по журналу и заморозка из-за спорного платежа;
 * assert-paywall-publishable.ts, ветка PROJECT_BLOCKED) и TOPUP_REQUIRES_PRO
 * (billing.service.ts:2941/3652). Прежнее условие `code !== 'TIER_LIMIT' &&
 * status !== 402` считало тарифным отказом ЛЮБОЙ 402 — человеку с долгом
 * организации показывали «купите PRO», он платил, а публикация всё равно не
 * проходила.
 *
 * Ревизия волны 23.08: FEATURE_LOCKED — ТАРИФНЫЙ отказ (WEB-613, гейт
 * «публикация закрыта тарифом FREE», funnels.service.ts:426 и
 * paywalls.service.ts:725). Первая правка WEB-1495 исключила его вместе с
 * нетарифными — визард печатал «перейдите на Pro» текстом, а окно покупки
 * тарифа не открывал, и перейти человеку было негде. Чисел usage/limit у
 * FEATURE_LOCKED нет — возвращаются нули, как у 402-без-кода (шрам WEB-135).
 *
 * Ревизия фикс-волны 23.08 — прежняя редакция этого комментария врала дважды:
 * (а) утверждала, что 402 без кода бывает лишь из-за шрама WEB-135, тогда как
 * сервер штатно отдавал безкодовые 402 («Insufficient balance»
 * billing.service.ts:563/2138, «Top-up is not available on the Free plan»
 * billing-math.ts:31); (б) объявляла список 402-кодов полным, пропустив
 * TRIAL_DOMAIN_BLOCKED (покупка/подключение домена в пробном периоде,
 * `purchaseDomain` / `purchaseDomainForOrg` в
 * backend/src/domains/services/purchase/domain-purchase.ts). Безкодовым 402 бэкенд в этой же волне выдал
 * коды INSUFFICIENT_BALANCE и TOPUP_REQUIRES_PRO — то есть эти отказы теперь
 * узнаются как нетарифные ПО КОДУ, а не проваливаются в ветку «402 без кода».
 *
 * Итог — полный список 402-кодов сервера (сверен по коду 23.08):
 *  · тарифные (окно покупки тарифа): TIER_LIMIT, FEATURE_LOCKED — при любом
 *    статусе, код мог доехать и без статуса;
 *  · НЕтарифные (человеку показывается серверная причина текстом):
 *    PROJECT_BLOCKED, TRIAL_DOMAIN_BLOCKED, INSUFFICIENT_BALANCE,
 *    TOPUP_REQUIRES_PRO — и любой другой чужой код;
 *  · 402 вовсе без кода — считаем тарифным (страховка WEB-135: старые прокси
 *    и перехватчики могут потерять код по дороге).
 */
export function asTierLimitRejection(
  error: unknown,
): TierLimitRejection | null {
  if (error === null || error === undefined) return null;

  const { status, code, reasonCode } = extractApiError(error);
  const isTierRefusal =
    code === 'TIER_LIMIT' ||
    // Отдельная тарифная ветка: гейт функции (публикации) тарифом.
    code === 'FEATURE_LOCKED' ||
    (status === 402 && code === undefined);
  if (!isTierRefusal) return null;

  const e = error as { usage?: number; limit?: number };
  return {
    usage: typeof e?.usage === 'number' ? e.usage : 0,
    limit: typeof e?.limit === 'number' ? e.limit : 0,
    reasonCode,
  };
}

/**
 * Гейты публикации, которые кабинет проверяет САМ, не спрашивая сервер
 * (кит `PublishButton`, меню строки в списках квизов/пейволлов/воронок).
 */
export type ClientPublishGate = 'no_domain' | 'empty_content' | 'tier';

/**
 * WEB-1908 (граница карточки, пачка 26) — ПРИЧИНА для окна тарифа, поднятого
 * клиентским пред-гейтом публикации.
 *
 * Возврат приёмки В11-Л6 научил окно печатать причину серверного отказа
 * (`reasonCode` из 402). Но на бесплатном тарифе до сервера дело не доходит:
 * кабинет сам знает, что публикация закрыта тарифом, и поднимает окно сразу
 * (`onPublishGate('tier')` в списках). Отказа не было — причины в этой ветке
 * тоже нет, и человек снова видит голое «Выберите тариф».
 *
 * Дверь одна и только здесь: страницы код руками не пишут (WEB-1844 — вторая
 * редакция кода разъедется с первой). Сам код взят из реестра
 * `backend/src/shared/errors/human-error-codes.ts` и знаком словарю
 * `i18n/dictionaries/errors.ts` обеими половинами — фразу окно берёт оттуда.
 *
 * Нетарифные гейты причины не получают осознанно: «нет домена» и «пустой
 * контент» окно тарифа не поднимают вовсе (см. обработчики `onPublishGate` —
 * они реагируют только на `'tier'`), и выдуманный код там врал бы про деньги.
 */
export function reasonCodeForClientGate(
  gate: ClientPublishGate,
): string | undefined {
  return gate === 'tier' ? 'PAYMENT_REQUIRED_PUBLISH_ON_FREE' : undefined;
}

/**
 * Ревизия пачки 26 (Ф4) — ПРИЧИНА для окна тарифа, поднятого клиентским
 * гейтом СОЗДАНИЯ.
 *
 * Раньше кнопка «создать» при исчерпанном лимите просто гасла: ветка «поднять
 * окно тарифа» внутри её обработчика была недостижима (по выключенной кнопке
 * клик не приходит), и человек видел серую кнопку без единого слова о
 * причине. Тариф кнопку больше не гасит — клик доходит и поднимает окно с
 * причиной. Серверного отказа в этой ветке не было, значит код причины даёт
 * кабинет, и ровно так же, как у пред-гейта публикации выше: дверь ОДНА,
 * страницы код руками не пишут (WEB-1844 — вторая редакция кода разъедется с
 * первой).
 *
 * Коды взяты из реестра `backend/src/shared/errors/human-error-codes.ts` — те
 * же, что шлёт сам сервер при отказе создания (`tier-enforcement.service.ts`),
 * — и знакомы словарю `i18n/dictionaries/errors.ts` обеими половинами.
 */
export const CREATE_LIMIT_KINDS = ['quiz', 'paywall', 'project'] as const;

export type CreateLimitKind = (typeof CREATE_LIMIT_KINDS)[number];

/**
 * Ревизия 26, свежий взгляд Н1 — ПРИЧИНУ ВЫБИРАЕТ ПАРА (ВИД, ТАРИФ).
 *
 * WEB-3840: общая квота воронок у «не оплачено» снята (решение владельца
 * 25.10, п. 54) — числа на обоих концах пары одинаковые (по виду), а коды
 * причины разные: без оплаты — коды «не оплачено», как шлёт сервер.
 *
 * Раньше код зависел только от вида сущности, и на бесплатном тарифе окно
 * врало: клиентский гейт там считает не квизы и не пейволлы, а ОБЩИЕ воронки
 * (`useTier`, правило WEB-1080/240 «одна воронка = один квиз ИЛИ один
 * пейволл»), поэтому при одном пейволле и нуле квизов «Создать квиз» давало
 * фразу «больше квизов нельзя» при счёте «0 из 1». Сервер в этой же ветке
 * шлёт `PAYMENT_REQUIRED_TIER_LIMIT_FUNNELS`
 * (`tier-enforcement.service.ts`, ветки CREATE_QUIZ/CREATE_PAYWALL при
 * `tier === FREE`), а по виду сущности — только на платных тарифах.
 *
 * Разрез тот же, что в `useTier` (WEB-1290): точное совпадение с 'FREE', а не
 * отрицание. Скрытый тариф ('UNKNOWN') идёт по платной ветке — общий счёт
 * воронок это правило ИМЕННО бесплатного тарифа, угадывать его нельзя.
 *
 * Лимит проектов разреза не имеет: у сервера в ветке CREATE_PROJECT код один
 * на все тарифы.
 */
const CREATE_LIMIT_REASONS: Readonly<
  Record<CreateLimitKind, { readonly free: string; readonly paid: string }>
> = {
  // WEB-3840 (решения владельца 25.10, п. 54, 94): общей квоты воронок у
  // «не оплачено» больше нет — счёт по виду сущности на всех тарифах. Без
  // оплаты сервер на упоре отвечает своими кодами «не оплачено» (до 50
  // квизов и пейволлов, один проект; `unpaidRefusal` в
  // tier-enforcement.service.ts) — окно говорит то же (возврат приёмки
  // 25.10 15:15), а не «на вашем тарифе больше действующих квизов нельзя».
  quiz: {
    free: 'UNPAID_QUIZ_LIMIT',
    paid: 'PAYMENT_REQUIRED_TIER_LIMIT_QUIZZES',
  },
  paywall: {
    free: 'UNPAID_PAYWALL_LIMIT',
    paid: 'PAYMENT_REQUIRED_TIER_LIMIT_PAYWALLS',
  },
  // Третью дверь нашёл сам сторож двух концов: гейт лимита проектов в
  // мастере создания проекта поднимал окно тарифа так же молча.
  project: {
    free: 'UNPAID_PROJECT_LIMIT',
    paid: 'PAYMENT_REQUIRED_TIER_LIMIT_PROJECTS',
  },
};

/**
 * Все коды, которые дверь способна отдать. Выведены из самого реестра, а не
 * переписаны списком руками (шрам WEB-1687): сторож «код строкой в страницах
 * не пишется» обязан узнать про новый код сам.
 */
export const CREATE_LIMIT_CODES: readonly string[] = [
  ...new Set(
    Object.values(CREATE_LIMIT_REASONS).flatMap((pair) => [
      pair.free,
      pair.paid,
    ]),
  ),
];

export function reasonCodeForCreateLimit(
  kind: CreateLimitKind,
  tier: BillingTier,
): string {
  const pair = CREATE_LIMIT_REASONS[kind];
  return tier === 'FREE' ? pair.free : pair.paid;
}

/**
 * Ревизия 26, свежий взгляд Н1 — ЧИСЛА ОКНА БЕРУТСЯ ТЕМ ЖЕ РАЗРЕЗОМ.
 *
 * Причина и числа обязаны быть об одном и том же счётчике: фраза про воронки
 * рядом с «0 из 1» по квизам — то же самое враньё, только цифрами. Дверь
 * одна и та же, чтобы разрез не переписывали на каждой странице заново
 * (WEB-1844).
 */
const CREATE_LIMIT_COUNTERS: Readonly<
  Record<
    CreateLimitKind,
    {
      readonly free: (u: TierUsage, l: TierLimits) => readonly [number, number];
      readonly paid: (u: TierUsage, l: TierLimits) => readonly [number, number];
    }
  >
> = {
  // WEB-3840: числа — тем же счётом по виду, что причина выше.
  quiz: {
    free: (u, l) => [u.quizzes, l.maxQuizzes],
    paid: (u, l) => [u.quizzes, l.maxQuizzes],
  },
  paywall: {
    free: (u, l) => [u.paywalls, l.maxPaywalls],
    paid: (u, l) => [u.paywalls, l.maxPaywalls],
  },
  project: {
    free: (u, l) => [u.projects, l.maxProjects],
    paid: (u, l) => [u.projects, l.maxProjects],
  },
};

export interface CreateLimitCounters {
  readonly usage: number;
  readonly limit: number;
}

export function createLimitCounters(
  kind: CreateLimitKind,
  tier: BillingTier,
  usage: TierUsage | null,
  limits: TierLimits | null,
): CreateLimitCounters {
  if (usage == null || limits == null) return { usage: 0, limit: 0 };
  const pick = CREATE_LIMIT_COUNTERS[kind];
  const [used, limit] = (tier === 'FREE' ? pick.free : pick.paid)(
    usage,
    limits,
  );
  return { usage: used, limit };
}
